/** Turning a bundle a space holds into the added activities and wires it proposes (`CAT-009`). */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import type { ActivityManifest, BundleMember, ProposedBinding } from "@nebulize/sdk";

import { api } from "@/lib/api";
import { spaces } from "@/lib/spaces";

import type { AddedActivityRow } from "../../types";

import type { Placement } from "./arrangement";
import { placementOf } from "./arrangement";

/** One member still to add, and the preset the bundle wrote for it. */
export interface Missing {
  readonly name: string;
  readonly activity_id: string;
  readonly version: string;
  readonly settings: Record<string, unknown>;
}

/** What one materialise did. */
export interface Materialised {
  readonly added: number;
  readonly wired: number;
  readonly arranged: number;
  readonly refusals: string[];
}

export interface MaterialiseCommand {
  readonly spaceId: string;
  readonly manifest: ActivityManifest;
  readonly rows: readonly AddedActivityRow[];
  /** The newest published version per activity id. */
  readonly newest: ReadonlyMap<string, string>;
}

/** A member of the bundle that no added activity serves yet. */
export function missingMembers(
  manifest: ActivityManifest,
  rows: readonly AddedActivityRow[],
): Missing[] {
  const held = new Set(rows.map((one) => one.activity_id));
  return (manifest.bundle?.members ?? [])
    .filter((one) => !held.has(one.activity_id))
    .map((one) => ({
      name: one.name ?? one.activity_id,
      activity_id: one.activity_id,
      version: one.version ?? "",
      settings: (one.settings ?? {}) as Record<string, unknown>,
    }));
}

function exactVersion(member: Missing, newest: ReadonlyMap<string, string>): string {
  return newest.get(member.activity_id) ?? member.version.replace(/^[\^~]/u, "");
}

/** Where the bundle says each member sits, by canvas kind then by activity id. */
function placements(members: readonly BundleMember[]): Map<string, Map<string, Placement>> {
  const byKind = new Map<string, Map<string, Placement>>();
  for (const member of members) {
    for (const [kind, held] of Object.entries(member.arrangement ?? {})) {
      const inKind = byKind.get(kind) ?? new Map<string, Placement>();
      inKind.set(member.activity_id, placementOf(held));
      byKind.set(kind, inKind);
    }
  }
  return byKind;
}

async function wire(
  spaceId: string,
  binding: ProposedBinding,
  byName: ReadonlyMap<string, string>,
): Promise<boolean> {
  const source = byName.get(binding.source);
  const target = byName.get(binding.target);
  if (source === undefined || target === undefined) return false;
  await api.post(`/v1/spaces/${spaceId}/event-bindings`, {
    source_added_activity_id: source,
    export: binding.export,
    target_added_activity_id: target,
    trigger: binding.trigger,
    parameters: {},
  });
  return true;
}

/**
 * Add every member this space does not hold, wire what the bundle proposed, and write the
 * placements it named. Each step is reported and none is rolled back.
 */
export function useMaterialise(): UseMutationResult<Materialised, Error, MaterialiseCommand> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ spaceId, manifest, rows, newest }: MaterialiseCommand) => {
      const refusals: string[] = [];
      const byName = new Map<string, string>();
      for (const member of manifest.bundle?.members ?? []) {
        const found = rows.find((one) => one.activity_id === member.activity_id);
        if (found !== undefined) {
          byName.set(member.name ?? member.activity_id, found.added_activity_id);
        }
      }

      let added = 0;
      for (const member of missingMembers(manifest, rows)) {
        try {
          const made = await spaces.addActivity(spaceId, {
            activity_id: member.activity_id,
            activity_version: exactVersion(member, newest),
            name: member.name,
            settings: member.settings,
          });
          byName.set(member.name, made.added_activity_id);
          added += 1;
        } catch (error) {
          refusals.push(`${member.name}: ${error instanceof Error ? error.message : "refused"}`);
        }
      }

      let wired = 0;
      for (const binding of manifest.bundle?.proposed_bindings ?? []) {
        if (binding.enabled_by_default === false) continue;
        try {
          if (await wire(spaceId, binding, byName)) wired += 1;
        } catch (error) {
          const said = error instanceof Error ? error.message : "refused";
          refusals.push(`${binding.source} to ${binding.target}: ${said}`);
        }
      }

      const idOf = new Map<string, string>();
      for (const member of manifest.bundle?.members ?? []) {
        const id = byName.get(member.name ?? member.activity_id);
        if (id !== undefined) idOf.set(member.activity_id, id);
      }

      let arranged = 0;
      for (const [kind, held] of placements(manifest.bundle?.members ?? [])) {
        const body = [...held]
          .map(([activityId, placement]) => {
            const id = idOf.get(activityId);
            return id === undefined ? null : { added_activity_id: id, ...placement };
          })
          .filter((one): one is NonNullable<typeof one> => one !== null);
        if (body.length === 0) continue;
        try {
          await api.put(
            `/v1/spaces/${spaceId}/canvases/${encodeURIComponent(kind)}/arrangement`,
            { placements: body },
          );
          arranged += body.length;
        } catch (error) {
          refusals.push(`${kind}: ${error instanceof Error ? error.message : "refused"}`);
        }
      }

      return { added, wired, arranged, refusals };
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["spaces"] });
    },
  });
}
